import React, { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useMap } from './map-context';

const MapContainer: React.FC = () => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const { map } = useMap();

  useEffect(() => {
    const initializeMap = () => {
      if (!map || !mapElementRef.current) {
        console.log('Map or mapElement not ready, retrying...');
        return;
      }

      try {
        map.setTarget(mapElementRef.current);
        map.updateSize(); // Force map to update its size

        // Add a resize observer to handle container size changes
        const resizeObserver = new ResizeObserver(() => {
          if (map && mapElementRef.current) {
            map.updateSize();
          }
        });

        resizeObserver.observe(mapElementRef.current);

        return () => {
          resizeObserver.disconnect();
        };
      } catch (error) {
        console.error('Error initializing map:', error);
        return undefined;
      }
    };

    // Try to initialize immediately
    const cleanup = initializeMap();

    // If initialization failed, retry after a short delay
    if (!cleanup) {
      const timeoutId = setTimeout(() => {
        initializeMap();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return cleanup;
  }, [map]);

  return (
    <Box
      ref={mapElementRef}
      w='full'
      h='full'
      position='absolute'
      top='0'
      left='0'
      right='0'
      bottom='0'
      overflow='hidden'
      bg='gray.100'
      id='map-container'
    />
  );
};

export default MapContainer;
